// WHAT: Card component for displaying SwitchBot device information and sensor data
// WHY:  Provides comprehensive view of device status, sensor readings, and calculated humidity metrics
// DOMAIN: SmartHome → SwitchBot
// DEPENDENCIES: SwitchBotDevice, SwitchBotDeviceStatus types
// RISKS:
//   - NOTE: Humidity calculations use scientific formulas - ensure input values are valid
//   - NOTE: Temperature/humidity color coding assumes specific ranges - may not match all use cases
//   - NOTE: Device type detection uses string includes() - may match unintended types

import { type SwitchBotDevice, type SwitchBotDeviceStatus } from "@/domains/smarthome/services";

/**
 * Props for SwitchBotDeviceCard component
 */
interface SwitchBotDeviceCardProperties {
  /** SwitchBot device to display */
  readonly device: SwitchBotDevice;
  /** Optional device status (sensor readings, battery, etc.) */
  readonly status?: SwitchBotDeviceStatus;
}

/**
 * Local, strikt typisierte Sicht auf ein SwitchBot-Device.
 * Verhindert, dass ein extern als `any/error` getypter Typ direkt verwendet wird.
 */
interface SwitchBotDeviceView {
  deviceId: string;
  deviceName: string;
  deviceType: string;
  hubDeviceId?: string;
}

/**
 * Lokale, strikt typisierte Sicht auf den Gerätestatus (Sensorwerte).
 */
interface SwitchBotStatusView {
  battery: number;
  humidity: number;
  temperature: number;
  version: string;
}

const toDeviceView = (value: unknown): SwitchBotDeviceView => {
  if (!value || typeof value !== "object") {
    return {
      deviceId: "",
      deviceName: "",
      deviceType: "",
    };
  }

  const raw = value as Record<string, unknown>;

  const deviceType = typeof raw.deviceType === "string" ? raw.deviceType : "";
  const deviceName = typeof raw.deviceName === "string" ? raw.deviceName : "";
  const deviceId = typeof raw.deviceId === "string" ? raw.deviceId : "";
  const hubDeviceId = typeof raw.hubDeviceId === "string" ? raw.hubDeviceId : undefined;

  const result: SwitchBotDeviceView = {
    deviceId,
    deviceName,
    deviceType,
  };

  if (hubDeviceId !== undefined) {
    result.hubDeviceId = hubDeviceId;
  }

  return result;
};

const toStatusView = (value: unknown): SwitchBotStatusView | undefined => {
  if (!value || typeof value !== "object") {
    return undefined;
  }

  const raw = value as Record<string, unknown>;

  const temperature = typeof raw.temperature === "number" ? raw.temperature : undefined;
  const humidity = typeof raw.humidity === "number" ? raw.humidity : undefined;
  const battery = typeof raw.battery === "number" ? raw.battery : undefined;
  const version = typeof raw.version === "string" ? raw.version : undefined;

  if (
    temperature === undefined &&
    humidity === undefined &&
    battery === undefined &&
    version === undefined
  ) {
    return undefined;
  }

  return {
    battery: battery ?? 0,
    humidity: humidity ?? 0,
    temperature: temperature ?? 0,
    version: version ?? "",
  };
};

/**
 * WHAT:
 *   Calculates derived humidity metrics from temperature and relative humidity.
 *
 * WHY:
 *   Provides additional humidity metrics (absolute humidity, dew point, VPD) that are
 *   useful for environmental monitoring and plant care applications.
 *
 * BEHAVIOR:
 *   - Calculates absolute humidity (g/m³)
 *   - Calculates dew point (°C)
 *   - Calculates VPD (Vapor Pressure Deficit in kPa)
 *   - Rounds values to appropriate precision
 *
 * RISKS:
 *   - NOTE: Uses scientific formulas - ensure input values are in correct units
 *   - NOTE: Division by zero possible if temperature is -273.15°C (absolute zero)
 *   - NOTE: Logarithm may fail if vapor pressure is negative or zero
 *
 * @param {number} temperature - Temperature in Celsius
 *
 * @param {number} relativeHumidity - Relative humidity in percent (0-100)
 *
 * @returns {object} Object with absoluteHumidity, dewPoint, and vpd
 *
 */
const calculateHumidityValues = (
  temperature: number,
  relativeHumidity: number,
): {
  absoluteHumidity: number;
  dewPoint: number;
  vpd: number;
} => {
  const saturationVaporPressure = 6.112 * Math.exp((17.67 * temperature) / (temperature + 243.5));
  const actualVaporPressure = saturationVaporPressure * (relativeHumidity / 100);

  const absoluteHumidity =
    (((relativeHumidity / 100) * saturationVaporPressure * 2.166) / (temperature + 273.15)) * 100; // korrekt skaliert auf g/m³

  const dewPoint =
    (243.5 * Math.log(actualVaporPressure / 6.112)) /
    (17.67 - Math.log(actualVaporPressure / 6.112));

  const es_kPa = 0.6108 * Math.exp((17.27 * temperature) / (temperature + 237.3));
  const ea_kPa = es_kPa * (relativeHumidity / 100);
  const vpd = es_kPa - ea_kPa;

  return {
    absoluteHumidity: Math.round(absoluteHumidity * 100) / 100,
    dewPoint: Math.round(dewPoint * 10) / 10,
    vpd: Math.round(vpd * 1000) / 1000,
  };
};

/**
 * WHAT:
 *   Returns emoji icon for a given device type.
 *
 * WHY:
 *   Provides visual identification of device types in the UI.
 *
 * BEHAVIOR:
 *   - Returns hub icon for Hub devices
 *   - Returns thermometer for WoIOSensor
 *   - Returns default icon for other types
 *
 * RISKS:
 *   - NOTE: Uses string includes() - "Hub" may match unintended device types
 *
 * @param {string} deviceType - Device type string
 *
 * @returns {string} Emoji icon for the device type
 *
 */
const getDeviceIcon = (deviceType: string): string => {
  if (deviceType.includes("Hub")) {
    return "🏠";
  }
  if (deviceType === "WoIOSensor") {
    return "🌡️";
  }
  return "📱";
};

/**
 * WHAT:
 *   Returns color class for battery level indication.
 *
 * WHY:
 *   Provides visual feedback on battery status (low/medium/high).
 *
 * BEHAVIOR:
 *   - Returns red for ≤20%
 *   - Returns orange for ≤40%
 *   - Returns yellow for ≤60%
 *   - Returns green for >60%
 *
 * RISKS:
 *   - NOTE: Thresholds are hardcoded - may not match user preferences
 *
 * @param {number} [battery] - Battery level in percent (0-100)
 *
 * @returns {string} Tailwind CSS color class
 *
 */
const getBatteryColor = (battery?: number): string => {
  if (!battery) {
    return "text-slate-400";
  }
  if (battery <= 20) {
    return "text-red-400";
  }
  if (battery <= 40) {
    return "text-orange-400";
  }
  if (battery <= 60) {
    return "text-yellow-400";
  }
  return "text-green-400";
};

/**
 * WHAT:
 *   Returns color class for temperature indication.
 *
 * WHY:
 *   Provides visual feedback on temperature ranges (cold/normal/hot).
 *
 * BEHAVIOR:
 *   - Returns blue for <10°C
 *   - Returns red for >30°C
 *   - Returns green for 10-30°C
 *
 * RISKS:
 *   - NOTE: Thresholds assume indoor temperature ranges - may not match all use cases
 *
 * @param {number} [temperature] - Temperature in Celsius
 *
 * @returns {string} Tailwind CSS color class
 *
 */
const getTemperatureColor = (temperature?: number): string => {
  if (temperature === undefined) {
    return "text-slate-400";
  }
  if (temperature < 10) {
    return "text-blue-400";
  }
  if (temperature > 30) {
    return "text-red-400";
  }
  return "text-green-400";
};

/**
 * WHAT:
 *   Card component that displays SwitchBot device information including device details,
 *   sensor readings (for WoIOSensor), and calculated humidity metrics.
 *
 * WHY:
 *   Provides a comprehensive view of device status and sensor data, helping users
 *   monitor their smart home devices and environmental conditions.
 *
 * PROPS:
 *   - device: SwitchBotDevice — Device to display
 *   - status?: SwitchBotDeviceStatus — Optional device status
 *
 * BEHAVIOR:
 *   - Displays device name, type, and ID
 *   - Shows sensor readings for WoIOSensor devices (temperature, humidity, battery)
 *   - Calculates and displays derived humidity metrics (absolute humidity, dew point, VPD)
 *   - Shows hub status for Hub devices
 *   - Displays hub connection information
 *
 * RISKS:
 *   - NOTE: Device type detection uses string includes() - may match unintended types
 *   - NOTE: Color coding assumes specific temperature/battery ranges
 *
 * @param {SwitchBotDeviceCardProperties} props - Component props
 *
 * @param {SwitchBotDevice} props.device - SwitchBot device to display
 *
 * @param {SwitchBotDeviceStatus} [props.status] - Optional device status
 *
 * @returns {JSX.Element} SwitchBot device card component
 *
 */
export function SwitchBotDeviceCard({ device, status }: SwitchBotDeviceCardProperties) {
  const deviceView = toDeviceView(device);
  const statusView = toStatusView(status);
  // NOTE: Check if device is a WoIOSensor
  const isWoIOSensor = deviceView.deviceType === "WoIOSensor";
  // NOTE: Check if device is a Hub (uses includes for flexibility)
  const isHub = typeof deviceView.deviceType === "string" && deviceView.deviceType.includes("Hub");

  // NOTE: Check if humidity data is available for calculations
  const hasHumidityData =
    statusView !== undefined &&
    typeof statusView.temperature === "number" &&
    typeof statusView.humidity === "number";
  // NOTE: Calculate derived humidity metrics if data is available
  const humidityValues = hasHumidityData
    ? calculateHumidityValues(statusView.temperature, statusView.humidity)
    : null;

  return (
    <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-6 border border-slate-700 hover:border-slate-600 transition-all duration-300 shadow-lg hover:shadow-xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="text-2xl">{getDeviceIcon(deviceView.deviceType || "Unknown")}</div>
          <div>
            <h3 className="text-lg font-semibold text-white">
              {deviceView.deviceName || deviceView.deviceId || "Unknown Device"}
            </h3>
            <p className="text-sm text-slate-400">{deviceView.deviceType || "Unknown Type"}</p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs text-slate-500">Device ID</div>
          <div className="text-xs text-slate-400 font-mono">{deviceView.deviceId || "N/A"}</div>
        </div>
      </div>

      {/* Status für WoIOSensor */}
      {isWoIOSensor && statusView ? (
        <WoIOSensorStatus
          hasHumidityData={hasHumidityData}
          humidityValues={humidityValues}
          statusView={statusView}
        />
      ) : null}

      {/* Hub Info */}
      {isHub ? (
        <InfoCard color="text-green-400" icon="🔗" label="Hub Status" value="Online" />
      ) : null}

      {/* Footer */}
      <div className="mt-4 pt-3 border-t border-slate-700">
        <div className="text-xs text-slate-500">
          Hub:{" "}
          {deviceView.hubDeviceId === "000000000000"
            ? "Direkt verbunden"
            : (deviceView.hubDeviceId ?? "Unbekannt")}
        </div>
      </div>
    </div>
  );
}

/**
 * Props for InfoCard component
 */
interface InfoCardProperties {
  /** Optional color class for value text */
  readonly color?: string;
  /** Emoji icon to display */
  readonly icon: string;
  /** Label text */
  readonly label: string;
  /** Value text */
  readonly value: string;
}

/**
 * WHAT:
 *   Reusable card component for displaying labeled information with icon.
 *
 * WHY:
 *   Provides consistent styling for displaying sensor data and device information.
 *
 * BEHAVIOR:
 *   - Displays icon, label, and value
 *   - Applies optional color class to value
 *   - Uses consistent card styling
 *
 * @param {InfoCardProperties} props - Component props
 *
 * @param {string} props.icon - Emoji icon to display
 *
 * @param {string} props.label - Label text
 *
 * @param {string} props.value - Value text
 *
 * @param {string} [props.color] - Optional color class for value text
 *
 * @returns {JSX.Element} Info card component
 *
 */
function InfoCard({ color, icon, label, value }: InfoCardProperties) {
  return (
    <div className="bg-slate-700/50 rounded-lg p-3">
      <div className="flex items-center space-x-2">
        <span className="text-lg">{icon}</span>
        <div>
          <div className="text-xs text-slate-400">{label}</div>
          <div className={`text-lg font-semibold ${color ?? "text-slate-400"}`}>{value}</div>
        </div>
      </div>
    </div>
  );
}

/**
 * WHAT:
 *   Component for displaying WoIOSensor status information.
 *
 * WHY:
 *   Extracts complex JSX rendering logic to reduce cognitive complexity.
 *
 * @param {object} props - Component props
 *
 * @param {boolean} props.hasHumidityData - Whether humidity data is available
 *
 * @param {object | null} props.humidityValues - Calculated humidity values
 *
 * @param {SwitchBotStatusView} props.statusView - Status view data
 *
 * @returns {JSX.Element | null} WoIOSensor status component
 *
 */
/**
 * WHAT:
 *   Component for displaying WoIOSensor status information.
 *
 * WHY:
 *   Extracts complex JSX rendering logic to reduce cognitive complexity.
 *
 * @param {object} props - Component props
 *
 * @param {boolean} props.hasHumidityData - Whether humidity data is available
 *
 * @param {object | null} props.humidityValues - Calculated humidity values
 *
 * @param {SwitchBotStatusView} props.statusView - Status view data
 *
 * @returns {JSX.Element | null} WoIOSensor status component
 *
 */
function WoIOSensorStatus({
  hasHumidityData,
  humidityValues,
  statusView,
}: {
  readonly hasHumidityData: boolean;
  readonly humidityValues: null | {
    absoluteHumidity: number;
    dewPoint: number;
    vpd: number;
  };
  readonly statusView: SwitchBotStatusView;
}) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-4">
        {/* Temperatur */}
        <InfoCard
          color={getTemperatureColor(statusView.temperature)}
          icon="🌡️"
          label="Temperatur"
          value={
            typeof statusView.temperature === "number"
              ? `${String(statusView.temperature)}°C`
              : "N/A"
          }
        />

        {/* Luftfeuchtigkeit */}
        <InfoCard
          color="text-blue-400"
          icon="💧"
          label="Luftfeuchtigkeit"
          value={
            typeof statusView.humidity === "number" ? `${String(statusView.humidity)}%` : "N/A"
          }
        />
      </div>

      {hasHumidityData && humidityValues ? (
        <>
          <InfoCard
            color="text-cyan-400"
            icon="🌫️"
            label="Absolute Luftfeuchtigkeit"
            value={`${String(humidityValues.absoluteHumidity)} g/m³`}
          />

          <div className="grid grid-cols-2 gap-4">
            <InfoCard
              color="text-blue-400"
              icon="💧"
              label="Taupunkt"
              value={`${String(humidityValues.dewPoint)}°C`}
            />
            <InfoCard
              color="text-orange-400"
              icon="🌪️"
              label="VPD"
              value={`${String(humidityValues.vpd)} kPa`}
            />
          </div>
        </>
      ) : null}

      {/* Batterie und Version */}
      <div className="grid grid-cols-2 gap-4">
        <InfoCard
          color={getBatteryColor(statusView.battery)}
          icon="🔋"
          label="Batterie"
          value={typeof statusView.battery === "number" ? `${String(statusView.battery)}%` : "N/A"}
        />

        {statusView.version ? (
          <InfoCard color="text-purple-400" icon="📱" label="Version" value={statusView.version} />
        ) : null}
      </div>
    </div>
  );
}
